import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Toolbar } from '@devexpress/dx-react-grid-material-ui';
import {
  Button,
  ButtonGroup,
  CircularProgress,
  ClickAwayListener,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip
} from '@mui/material';

const ITEM_ACTIONS = [
  { value: 'delete', label: 'Delete' },
  { value: 'active', label: 'Make Active' },
  { value: 'disable', label: 'Disable' },
  { value: 'enable', label: 'Enable' }
];

const useStyles = makeStyles((theme) => ({
  toolbar: {
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    padding: theme.spacing(1),
    '& > div:first-of-type': {
      display: 'none'
    }
  }
}));

const ItemBulkActionGroup = ({ selectedCount, loading, onAction }) => {
  const [open, setOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(ITEM_ACTIONS[0].value);
  const anchorRef = useRef(null);
  const selectedOption = ITEM_ACTIONS.find(({ value }) => value === selectedAction) || ITEM_ACTIONS[0];

  const closeMenu = (event) => {
    if (anchorRef.current?.contains(event.target)) return;
    setOpen(false);
  };

  return (
    <React.Fragment>
      <ButtonGroup ref={anchorRef} variant="contained" size="small" aria-label="Item bulk actions">
        <Button disabled={!selectedCount || loading} onClick={() => onAction(selectedOption.value)}>
          {loading ? (
            <React.Fragment>
              <CircularProgress size={16} color="inherit" className="tw-mr-2" />
              {selectedOption.label}…
            </React.Fragment>
          ) : (
            `${selectedOption.label} (${selectedCount})`
          )}
        </Button>
        <Button
          size="small"
          aria-controls={open ? 'item-bulk-action-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
          aria-label="Choose item bulk action"
          disabled={loading}
          onClick={() => setOpen((current) => !current)}
          className="tw-z-50"
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-end" transition style={{ zIndex: 1400 }}>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={closeMenu}>
                <MenuList id="item-bulk-action-menu" autoFocusItem>
                  {ITEM_ACTIONS.filter(({ value }) => value !== selectedAction).map((action) => (
                    <MenuItem
                      key={action.value}
                      onClick={() => {
                        setSelectedAction(action.value);
                        setOpen(false);
                      }}
                    >
                      {action.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
};

const CustomToolbarRoot = ({
  children,
  total,
  selectedCount,
  refreshing,
  actionLoading,
  onRefresh,
  onDelete,
  onMakeActive,
  onDisable,
  onEnable,
  ...props
}) => {
  const classes = useStyles();
  const runAction = (action) => {
    if (action === 'delete') return onDelete();
    if (action === 'active') return onMakeActive();
    if (action === 'disable') return onDisable();
    return onEnable();
  };

  return (
    <Toolbar.Root {...props} className={classes.toolbar}>
      <strong>{total} items</strong>
      {children}
      <strong>{selectedCount} selected</strong>
      <Tooltip title="Refresh items">
        <span>
          <IconButton onClick={onRefresh} disabled={refreshing} size="small">
            <RefreshIcon className={refreshing ? 'tw-animate-spin' : ''} />
          </IconButton>
        </span>
      </Tooltip>
      <span style={{ display: 'inline-flex' }}>
        <ItemBulkActionGroup selectedCount={selectedCount} loading={actionLoading} onAction={runAction} />
      </span>
    </Toolbar.Root>
  );
};

ItemBulkActionGroup.propTypes = {
  selectedCount: PropTypes.number,
  loading: PropTypes.bool,
  onAction: PropTypes.func.isRequired
};

CustomToolbarRoot.propTypes = {
  children: PropTypes.any,
  total: PropTypes.number,
  selectedCount: PropTypes.number,
  refreshing: PropTypes.bool,
  actionLoading: PropTypes.bool,
  onRefresh: PropTypes.func,
  onDelete: PropTypes.func,
  onMakeActive: PropTypes.func,
  onDisable: PropTypes.func,
  onEnable: PropTypes.func
};

CustomToolbarRoot.defaultProps = {
  total: 0,
  selectedCount: 0,
  refreshing: false,
  actionLoading: false,
  onRefresh: () => {},
  onDelete: () => {},
  onMakeActive: () => {},
  onDisable: () => {},
  onEnable: () => {}
};

export default CustomToolbarRoot;
